import type { Acquisition } from '@nt/data/schema';
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FilterSheet } from './FilterSheet.tsx';
import { Legend } from './Legend.tsx';
import type { MapHandle } from './Map.tsx';
import { MobileViewToggle } from './MobileViewToggle.tsx';
import { Sidebar } from './Sidebar.tsx';
import {
  CATEGORIES,
  type Category,
  MONTH_KEYS,
  MONTH_LABEL,
  type MonthData,
  type MonthKey,
  loadMonth,
} from './data.ts';
import { useUrlState } from './url-state.ts';
import { useReducedMotion } from './use-reduced-motion.ts';

// Lazy-load MapView so the Leaflet + clustering bundle doesn't block first
// paint. Header and sidebar render immediately; the map chunk downloads in
// parallel and fills in once ready.
const MapView = lazy(() => import('./Map.tsx'));

const EMPTY_MONTH: MonthData = {
  key: '2026-03',
  label: '',
  records: [],
  placesById: {},
  classificationsById: {},
};

// requestIdleCallback isn't in Safari yet; polyfill to a short timeout.
const ric: (cb: () => void) => number =
  typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? (cb) => window.requestIdleCallback(cb, { timeout: 2000 })
    : (cb) => window.setTimeout(cb, 500);
const cic: (id: number) => void =
  typeof window !== 'undefined' && 'cancelIdleCallback' in window
    ? (id) => window.cancelIdleCallback(id)
    : (id) => window.clearTimeout(id);

export function App() {
  const [url, setUrl] = useUrlState();
  const { view, month: monthKey, id: selectedId, q: searchQuery, cats: filter } = url;

  const reducedMotion = useReducedMotion();

  const [month, setMonth] = useState<MonthData>(EMPTY_MONTH);
  const [loading, setLoading] = useState(true);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const mapRef = useRef<MapHandle>(null);

  // Load the selected month's data (cached in loadMonth after first fetch).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadMonth(monthKey).then((m) => {
      if (!cancelled) {
        setMonth(m);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [monthKey]);

  // When idle, preload adjacent months so prev/next nav is instant.
  useEffect(() => {
    if (loading) return;
    const idx = MONTH_KEYS.indexOf(monthKey);
    const neighbors = [MONTH_KEYS[idx - 1], MONTH_KEYS[idx + 1]].filter(
      (k): k is MonthKey => k !== undefined,
    );
    const id = ric(() => {
      for (const k of neighbors) {
        // loadMonth returns immediately from cache on second call; first call
        // triggers the background download+parse.
        void loadMonth(k);
      }
    });
    return () => cic(id);
  }, [monthKey, loading]);

  const resetView = useCallback(() => {
    // H1 click resets the map view *and* clears the URL selection so the
    // URL state matches what the user sees: no marker selected, map at the
    // default world view. Search and filters are preserved.
    setUrl({ id: null });
    mapRef.current?.reset();
  }, [setUrl]);

  const selectFromSidebar = useCallback(
    (id: string) => {
      // Row click expands inline details (toggle on second click). Switching
      // to the map view is the explicit job of the "Show on map →" button so
      // the two interactions stay distinct — see showOnMap below.
      setUrl((prev) => (prev.id === id ? { id: null } : { id }));
    },
    [setUrl],
  );

  const showOnMap = useCallback(
    (id: string) => {
      setUrl({ id, view: 'map' });
    },
    [setUrl],
  );

  // Stable reference — the marker layer's useEffect depends on this. Marker
  // clicks fire popupopen, which writes the id to the URL. The map-driver
  // effect above will then call openMarker(id), which is a no-op since the
  // popup is already open.
  const selectFromMarker = useCallback(
    (id: string) => {
      setUrl({ id });
    },
    [setUrl],
  );

  const setView = useCallback(
    (next: 'map' | 'list') => {
      setUrl({ view: next });
    },
    [setUrl],
  );

  const setSearchQuery = useCallback(
    (q: string) => {
      // Replace history entry on each keystroke so back-button doesn't pop
      // through every character typed.
      setUrl({ q }, { replace: true });
    },
    [setUrl],
  );

  const setMonthKey = useCallback(
    (k: MonthKey) => {
      // Switching months invalidates the current selection and search context.
      setUrl({ month: k, id: null, q: '' });
    },
    [setUrl],
  );

  const toggleCategory = useCallback(
    (cat: Category) => {
      setUrl((prev) => {
        const cats = new Set(prev.cats);
        if (cats.size === CATEGORIES.length) {
          // First click after "all selected" enters single-select mode.
          return { cats: new Set([cat]) };
        }
        if (cats.has(cat)) cats.delete(cat);
        else cats.add(cat);
        // Empty selection is invalid — leave the previous filter alone.
        if (cats.size === 0) return {};
        return { cats };
      });
    },
    [setUrl],
  );

  const resetFilter = useCallback(() => {
    // "Reset" means reset the whole user-controlled state — filters back to
    // all categories, selection cleared, AND the map view back to the
    // default. Without the view reset, users would have to take a second
    // step (the map's home control) to actually return to the starting
    // position.
    setUrl({ cats: new Set(CATEGORIES), id: null });
    mapRef.current?.reset();
  }, [setUrl]);

  const visibleByCategory = useMemo(() => {
    return month.records.filter((r) => {
      const cat = month.classificationsById[r.id];
      return cat ? filter.has(cat) : true;
    });
  }, [month, filter]);

  // For the map: only records with a resolved place.
  const mappable = useMemo(
    () => visibleByCategory.filter((r) => month.placesById[r.id]).sort(byCallNumber),
    [visibleByCategory, month.placesById],
  );

  // For the sidebar: mappable first (by call number), then unmapped (also by call number).
  const sidebarBase = useMemo(() => {
    const unmapped = visibleByCategory.filter((r) => !month.placesById[r.id]).sort(byCallNumber);
    return { mapped: mappable, unmapped };
  }, [visibleByCategory, mappable, month.placesById]);

  const { sidebarRecords, firstUnmappedIndex } = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const match = (r: Acquisition) =>
      !q ||
      r.title.toLowerCase().includes(q) ||
      (r.call_number ?? '').toLowerCase().includes(q) ||
      r.authors.some((a) => a.toLowerCase().includes(q));
    const mapped = sidebarBase.mapped.filter(match);
    const unmapped = sidebarBase.unmapped.filter(match);
    return {
      sidebarRecords: [...mapped, ...unmapped],
      firstUnmappedIndex: mapped.length,
    };
  }, [sidebarBase, searchQuery]);

  const resolvedTotal = month.records.filter((r) => month.placesById[r.id]).length;
  const unmappedCount = sidebarBase.unmapped.length;

  return (
    <div className="app">
      <a className="skip-link" href="#record-list">
        Skip to record list
      </a>
      <header className="app-header">
        <div className="header-main">
          <h1>
            <button
              type="button"
              className="clickable-title"
              onClick={resetView}
              title="Reset map view"
            >
              ISAW Library New Titles
            </button>
          </h1>
          {/* biome-ignore lint/a11y/useSemanticElements: <output> is for form-result computations; this is an inline status subtitle. role="status" gives implicit aria-live="polite" + aria-atomic="true". */}
          <p className="subtle" role="status">
            {loading ? (
              <span>Loading {MONTH_LABEL[monthKey]}…</span>
            ) : (
              <>
                {mappable.length} mapped (of {resolvedTotal}) · {unmappedCount} unmapped in sidebar
                · {month.records.length} total
              </>
            )}
          </p>
        </div>
        <MonthNav monthKey={monthKey} onChange={setMonthKey} />
        <button
          type="button"
          className="filters-button"
          onClick={() => setFilterSheetOpen(true)}
          aria-label="Open filters"
          aria-haspopup="dialog"
          aria-expanded={filterSheetOpen}
          aria-controls="filter-sheet"
        >
          Filters
          {filter.size < CATEGORIES.length ? <span> · {filter.size}</span> : null}
        </button>
      </header>

      <Legend
        counts={categoryCounts(month.records, month.classificationsById)}
        filter={filter}
        onToggle={toggleCategory}
        onReset={resetFilter}
        allActive={filter.size === CATEGORIES.length}
      />

      <div className={`app-body ${view === 'map' ? 'show-map' : 'show-list'}`}>
        <Sidebar
          records={sidebarRecords}
          placesById={month.placesById}
          classificationsById={month.classificationsById}
          selectedId={selectedId}
          onSelect={selectFromSidebar}
          onShowOnMap={showOnMap}
          firstUnmappedIndex={firstUnmappedIndex}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <Suspense fallback={<div className="map map-loading">Loading map…</div>}>
          <MapView
            key={monthKey}
            ref={mapRef}
            records={mappable}
            placesById={month.placesById}
            classificationsById={month.classificationsById}
            selectedId={selectedId}
            visible={view === 'map'}
            reducedMotion={reducedMotion}
            onSelect={selectFromMarker}
          />
        </Suspense>
      </div>
      <MobileViewToggle tab={view} onChange={setView} />
      <FilterSheet
        open={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        counts={categoryCounts(month.records, month.classificationsById)}
        filter={filter}
        onToggle={toggleCategory}
        onReset={resetFilter}
      />
    </div>
  );
}

function byCallNumber(a: Acquisition, b: Acquisition): number {
  return (a.call_number ?? '').localeCompare(b.call_number ?? '');
}

function categoryCounts(
  records: readonly Acquisition[],
  classificationsById: Record<string, Category>,
): Record<Category, number> {
  const counts = Object.fromEntries(CATEGORIES.map((c) => [c, 0])) as Record<Category, number>;
  for (const r of records) {
    const cat = classificationsById[r.id];
    if (cat && cat in counts) counts[cat] += 1;
  }
  return counts;
}

function MonthNav({
  monthKey,
  onChange,
}: {
  monthKey: MonthKey;
  onChange: (k: MonthKey) => void;
}) {
  const idx = MONTH_KEYS.indexOf(monthKey);
  const atFirst = idx <= 0;
  const atLast = idx >= MONTH_KEYS.length - 1;
  function go(delta: number) {
    const next = MONTH_KEYS[idx + delta];
    if (next) onChange(next);
  }
  return (
    <div className="month-nav">
      <button
        type="button"
        className="month-nav-arrow"
        onClick={() => go(-1)}
        disabled={atFirst}
        aria-label="Previous month"
        title={
          atFirst
            ? 'No earlier month available'
            : `Go to ${MONTH_LABEL[MONTH_KEYS[idx - 1] as MonthKey]}`
        }
      >
        ‹
      </button>
      <span className="month-nav-label">{MONTH_LABEL[monthKey]}</span>
      <button
        type="button"
        className="month-nav-arrow"
        onClick={() => go(1)}
        disabled={atLast}
        aria-label="Next month"
        title={
          atLast
            ? 'No later month available'
            : `Go to ${MONTH_LABEL[MONTH_KEYS[idx + 1] as MonthKey]}`
        }
      >
        ›
      </button>
    </div>
  );
}
